import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Phone, CalendarCheck, UserPlus, ShoppingBag, Ticket, Repeat,
  Target, Radio, Gift, ShieldCheck, ArrowRight, Check,
  Compass, PenTool, LineChart, MessageSquare, Megaphone, Wand2,
  MonitorPlay, Plane, TramFront, Building2, Building, Dumbbell, Trophy, ShoppingCart, Fuel,
} from "lucide-react";
import { MarketingPage } from "../../components/smartreach/Chrome";
import SignalMap from "../../components/smartreach/SignalMap";

const COMPARE_THEM = [
  ["Optimizes for", "Clicks & impressions"],
  ["Audience", "Anyone searching a keyword"],
  ["Proof of results", "Reach & CTR"],
  ["Built for", "National ad budgets"],
];
const COMPARE_US = [
  ["Optimizes for", "Verified foot traffic"],
  ["Audience", "People physically at a competitor location"],
  ["Proof of results", "Confirmed store visits"],
  ["Built for", "Single & multi-location local businesses"],
];
const SIGNALS = [
  { tag: "SIGNAL 01 — T+0:00", h: "Detected at competitor", p: "A prospect's device is identified inside a competing location's geofence, in real time." },
  { tag: "SIGNAL 02 — T+0:06", h: "Targeted in the moment", p: "They're served your offer while they're still deciding where to spend — not weeks later." },
  { tag: "SIGNAL 03 — T+0:41", h: "Visit confirmed", p: "SmartReachAI verifies the walk-in at your location and reports it back. Attribution, not assumption." },
];
const WHY = [
  { n: "01", h: "Conquest the customers already in-market", p: "Target people standing at your competitor's location right now — not a cold audience that searched a keyword three weeks ago." },
  { n: "02", h: "Prove ROI in the metric that matters", p: "Foot-traffic attribution shows exactly how many targeted visits became real, in-store visits — not just clicks." },
  { n: "03", h: "Compete without a national budget", p: "Built for single-location and regional operators, not agencies managing million-dollar national campaigns." },
];
const INDUSTRIES = [
  ["Restaurants & QSR", "restaurants"], ["Real estate", "real-estate"], ["Commercial real estate", "commercial-real-estate"],
  ["Apartments & multifamily", "apartments"], ["Property management", "property-management"], ["Med spas & healthcare", "doctors"],
  ["Medical professionals (B2B)", "medical-professionals"],
  ["Auto dealerships", "auto"], ["Retail & grocery", "retail"], ["Gyms & fitness studios", "entertainment-events"],
  ["Home services", "home-services"], ["Professional services", "professional-services"],
];

// ---- classic content, re-skinned ----
const OUTCOMES = [
  { icon: MapPin, label: "Store visits" }, { icon: Phone, label: "Phone calls" },
  { icon: CalendarCheck, label: "Bookings" }, { icon: UserPlus, label: "Qualified leads" },
  { icon: ShoppingBag, label: "Sales" }, { icon: Ticket, label: "Event turnout" },
  { icon: Repeat, label: "Repeat customers" },
];
const STEPS = ["Understand", "Diagnose", "Recommend", "Build", "Approve", "Activate", "Learn", "Grow"];
const AGENTS = [
  { icon: Compass, t: "Growth Strategist", d: "Turns your goal & budget into an offer, channel mix, forecast and 90-day calendar — in minutes." },
  { icon: Target, t: "Local Market Analyst", d: "Reads your local demand, seasonality and competitors to find your best opportunity." },
  { icon: PenTool, t: "AI Ad Creation", d: "Writes on-brand ad concepts, headlines, scripts and landing copy across every channel — ready to review.", pop: true },
  { icon: Megaphone, t: "Media Buyer", d: "Optimizes pacing, bids, frequency and daypart inside the limits you approve." },
  { icon: LineChart, t: "Visit & Revenue Analyst", d: "Explains what produced visits, leads and sales — labeled by evidence level." },
  { icon: MessageSquare, t: "Lead Concierge", d: "Answers, qualifies, books and follows up with your leads within your rules and hours." },
];
const DIFF = [
  { icon: Target, title: "Competitor Conquesting Studio", to: "/competitor-targeting", body: "Target and measure around exact competitor storefronts using custom boundaries or the smallest supported radius — shown honestly before you launch." },
  { icon: MapPin, title: "Visit Intelligence", to: "/visit-intelligence", body: "See observed, modeled, and verified visits — never guesses dressed up as results. Full methodology and confidence included." },
  { icon: Radio, title: "FunAsia Radio + Digital", to: "/radio-digital", body: "Sync owned radio with digital and RadioLife.app offers so on-air moments become measurable visits and redemptions." },
];
const ENVIRONMENTS = [
  { title: "Connected TV & Streaming", note: "Representative premium streaming environments",
    items: [
      { name: "Disney+", slug: "disneyplus", color: "#113CCF" }, { name: "Netflix", slug: "netflix", color: "#E50914" },
      { name: "Prime Video", slug: "primevideo", color: "#00A8E1" }, { name: "Hulu", slug: "hulu", color: "#1CE783" },
      { name: "Peacock", slug: null, color: "#000000" }, { name: "Max", slug: null, color: "#002BE7" },
      { name: "Pluto TV", slug: null, color: "#1A1A5C" }, { name: "Tubi", slug: null, color: "#6C00E5" },
      { name: "Roku", slug: "roku", color: "#662D91" }, { name: "Fire TV", slug: null, color: "#FF9900" },
      { name: "Samsung TV Plus", slug: "samsung", color: "#1428A0" }, { name: "LG Channels", slug: "lg", color: "#A50034" },
      { name: "NBCUniversal", slug: null, color: "#6E55DC" },
    ] },
  { title: "Premium Websites & Trusted Publishers", note: "Representative premium publisher environments",
    items: [
      { name: "The New York Times", slug: null, color: "#000000" }, { name: "Bloomberg", slug: "bloomberg", color: "#000000" },
      { name: "Forbes", slug: "forbes", color: "#000000" }, { name: "The Guardian", slug: "theguardian", color: "#052962" },
      { name: "Realtor.com", slug: null, color: "#D92228" }, { name: "AARP", slug: null, color: "#E4002B" },
      { name: "Experian", slug: "experian", color: "#26478D" }, { name: "Kantar", slug: null, color: "#00A1E0" },
    ] },
  { title: "Social & Search Activation", note: "Integrated social and search platforms",
    items: [
      { name: "Facebook", slug: "facebook", color: "#1877F2" }, { name: "Instagram", slug: "instagram", color: "#E4405F" },
      { name: "Google", slug: "google", color: "#4285F4" }, { name: "YouTube", slug: "youtube", color: "#FF0000" },
      { name: "TikTok", slug: "tiktok", color: "#000000" }, { name: "LinkedIn", slug: "linkedin", color: "#0A66C2" },
      { name: "X", slug: "x", color: "#000000" },
    ] },
  { title: "Digital Out-of-Home", note: "Digital screen placements in public environments",
    items: [
      { name: "Billboards", icon: MonitorPlay }, { name: "Airports", icon: Plane }, { name: "Transit Hubs", icon: TramFront },
      { name: "Office Buildings", icon: Building2 }, { name: "Elevator Networks", icon: Building }, { name: "Gyms", icon: Dumbbell },
      { name: "Stadiums", icon: Trophy }, { name: "Live Event Venues", icon: Ticket }, { name: "Malls", icon: ShoppingBag },
      { name: "Grocery Stores", icon: ShoppingCart }, { name: "Gas Stations", icon: Fuel },
    ] },
];
const LOOP = [
  { icon: Radio, t: "Earn", d: "Listening codes, content, visits, purchases and referrals." },
  { icon: Gift, t: "Activate", d: "Time-bound merchant offers plus a FunAsia reward." },
  { icon: Check, t: "Redeem", d: "QR, POS, code, receipt or staff validation." },
  { icon: Repeat, t: "Repeat", d: "AI triggers replenishment and next-visit journeys." },
];
const MODES = [
  { t: "Self-Service", d: "AI builds the plan; you approve and launch." },
  { t: "AI-Assisted", d: "AI builds and optimizes; a specialist reviews exceptions.", pop: true },
  { t: "Professional Service", d: "An assigned team runs strategy, creative, media & radio." },
];

const Brand = ({ item }) => {
  const mono = (
    <span style={{ width: 20, height: 20, borderRadius: 5, background: item.color || "var(--x-ink)", color: "#fff",
      display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
      {item.name.replace(/^The /, "")[0]}
    </span>
  );
  return (
    <span className="srx-envchip" data-testid={`env-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      style={{ paddingLeft: 7, gap: 7 }}>
      {item.icon ? <item.icon size={16} color="var(--x-amber-dark)" />
        : item.slug ? (
          <span style={{ position: "relative", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <img src={`https://cdn.simpleicons.org/${item.slug}`} alt="" width={18} height={18} style={{ display: "block" }}
              onError={(e) => { e.currentTarget.style.display = "none"; const m = e.currentTarget.nextSibling; if (m) m.style.display = "inline-flex"; }} />
            {React.cloneElement(mono, { style: { ...mono.props.style, display: "none" } })}
          </span>
        ) : mono}
      {item.name}
    </span>
  );
};

const SecHead = ({ eyebrow, title, sub }) => (
  <div className="srx-head">
    <span className="srx-eyebrow">{eyebrow}</span>
    <h2>{title}</h2>
    {sub && <p>{sub}</p>}
  </div>
);

export default function Home() {
  return (
    <MarketingPage>
      {/* HERO */}
      <section className="srx-hero" data-testid="hero">
        <div className="srx-wrap srx-hero-inner">
          <div className="srx-hero-grid">
            <span className="srx-eyebrow">Real foot-traffic attribution</span>
            <h1>Your next customer is standing in your competitor&apos;s parking lot right now.</h1>
            <p className="srx-sub">
              SmartReachAI reaches real local foot traffic near competitor
              locations and helps you <strong>measure the store visits your campaigns influence</strong> — with
              clear modeled and verified evidence, not just clicks. Google and Meta optimize for online activity;
              we focus on what happens near your door.
            </p>
            <div className="srx-cta-row">
              <Link to="/signup" className="srx-btn-primary" data-testid="hero-primary-btn">Get your free trade-area report</Link>
              <Link to="/how-it-works" className="srx-btn-secondary" data-testid="hero-secondary-btn">See how it works</Link>
            </div>
          </div>
        </div>
        <div className="srx-hero-note">NO CREDIT CARD · SEE COMPETITOR TRADE-AREA SIGNAL BEFORE YOU SPEND A DOLLAR</div>
        <SignalMap />
      </section>

      {/* THE PROBLEM + COMPARISON */}
      <section className="srx-sec light" data-testid="problem">
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">The problem</span>
            <h2>Impressions don&apos;t pay your rent. Walk-ins do.</h2>
            <p>
              Google Ads and Meta were built for a world of clicks, not customers. They can tell you someone searched
              or scrolled — never whether they got in the car and drove to your business, or to your competitor&apos;s
              instead. For a brand with a thousand locations, that&apos;s an acceptable trade-off. For a single-location
              business, it&apos;s the whole business.
            </p>
          </div>
          <div className="srx-compare" data-testid="compare">
            <div className="srx-compare-col them">
              <div className="srx-compare-title">Google &amp; Meta</div>
              {COMPARE_THEM.map(([k, v]) => (
                <div className="srx-compare-row" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
              ))}
            </div>
            <div className="srx-compare-col us">
              <div className="srx-compare-title">SmartReachAI</div>
              {COMPARE_US.map(([k, v]) => (
                <div className="srx-compare-row" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES (from classic) */}
      <section className="srx-sec dark" data-testid="outcomes">
        <div className="srx-wrap">
          <SecHead eyebrow="Outcomes, not impressions" title="Grow the results your business actually cares about"
            sub="Choose a goal and SmartReachAI builds the plan, runs it, and measures the outcome." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
            {OUTCOMES.map((o) => (
              <div key={o.label} className="srx-ccard" data-testid={`outcome-${o.label.toLowerCase().replace(/\s/g, "-")}`}
                style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="srx-ico ink" style={{ marginBottom: 0, width: 38, height: 38 }}><o.icon size={18} /></span>
                <strong style={{ color: "#fff", fontSize: 15 }}>{o.label}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOT-TRAFFIC ATTRIBUTION — how it's different from Google & Meta */}
      <section className="srx-sec dark" id="tech" data-testid="tech" style={{ background: "var(--x-panel)" }}>
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">Foot-traffic attribution</span>
            <h2>We don&apos;t estimate foot traffic. We measure it — the way Google &amp; Meta can&apos;t.</h2>
            <p>
              SmartReachAI runs on premium programmatic foot-traffic attribution: we reach devices seen at
              competitor points of interest inside your trade area, then measure how many of those devices later
              show up at <em>your</em> location. That&apos;s a real-world visit — attribution, not assumption.
              Google and Meta measure clicks, impressions and on-platform activity; for a local business, none of
              that proves someone actually walked through your door.
            </p>
          </div>
          <div className="srx-timeline">
            {SIGNALS.map((s) => (
              <div className="srx-signal-card" key={s.tag} data-testid={`signal-${s.tag.match(/\d+/)[0]}`}>
                <span className="srx-signal-tag">{s.tag}</span>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
          <div className="srx-diff2" data-testid="foot-traffic-vs-bigtech">
            <div className="srx-diff2-card them">
              <h3>Why Google &amp; Meta fall short for local</h3>
              <ul>
                <li>Optimize for clicks, impressions and keyword searches</li>
                <li>Store-visit estimates are modeled and reserved for huge national budgets</li>
                <li>Can&apos;t target the person standing in a competitor&apos;s location right now</li>
                <li>Prove &quot;reach,&quot; not customers who actually walked in</li>
              </ul>
            </div>
            <div className="srx-diff2-card us">
              <h3>Why our foot-traffic attribution drives results</h3>
              <ul>
                <li><strong>In-market timing:</strong> reach people while they&apos;re actively choosing where to spend — the moment intent is highest</li>
                <li><strong>Competitor conquesting:</strong> win customers away from the business next door instead of a cold keyword audience</li>
                <li><strong>Measured proof:</strong> every dollar ties to confirmed store visits, labeled Observed / Modeled / Verified — so you optimize toward what actually pays rent</li>
                <li><strong>Built for local budgets:</strong> designed for single- and multi-location operators, not million-dollar national campaigns</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTFALL ATTRIBUTION SUMMARY — links to detail page */}
      <section className="srx-sec light" data-testid="footfall-summary">
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">Footfall Attribution</span>
            <h2>Prove the walk-ins your ads sent through the door</h2>
            <p>
              SmartReachAI.us Footfall Attribution measures real, in-store visits driven by your campaigns — with
              attributed visits, visitation lift and a true cost-per-visit. It&apos;s the ROI story your clients care
              about: not clicks, but customers who actually showed up.
            </p>
          </div>
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))" }}>
            <div className="srx-ccard" data-testid="footfall-summary-visits">
              <div style={{ fontFamily: "var(--x-fdisplay)", fontSize: 30, fontWeight: 800, color: "var(--x-amber-dark)" }}>3,052</div>
              <h3 style={{ marginTop: 6 }}>Attributed visits</h3>
              <p>Real customers who saw your ads and later walked in.</p>
            </div>
            <div className="srx-ccard" data-testid="footfall-summary-lift">
              <div style={{ fontFamily: "var(--x-fdisplay)", fontSize: 30, fontWeight: 800, color: "var(--x-amber-dark)" }}>+18%</div>
              <h3 style={{ marginTop: 6 }}>Visitation lift</h3>
              <p>Measured, statistically-validated increase vs. a control group.</p>
            </div>
            <div className="srx-ccard" data-testid="footfall-summary-cpv">
              <div style={{ fontFamily: "var(--x-fdisplay)", fontSize: 30, fontWeight: 800, color: "var(--x-amber-dark)" }}>$14.87</div>
              <h3 style={{ marginTop: 6 }}>Cost per visit</h3>
              <p>The true media cost of every confirmed in-store visit.</p>
            </div>
          </div>
          <div style={{ marginTop: 28 }}>
            <Link to="/footfall-attribution" className="srx-btn-primary" data-testid="footfall-learn-more-btn">
              Learn more about Footfall Attribution <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="srx-sec light" id="how" data-testid="why">
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">Why it matters</span>
            <h2>You don&apos;t need more reach. You need more customers walking through the door.</h2>
          </div>
          <div className="srx-why">
            {WHY.map((w) => (
              <div className="srx-why-card" key={w.n} data-testid={`why-${w.n}`}>
                <div className="srx-why-num">{w.n}</div>
                <h3>{w.h}</h3>
                <p>{w.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 8 STEPS (from classic) */}
      <section className="srx-sec dark" data-testid="steps">
        <div className="srx-wrap">
          <SecHead eyebrow="How it works" title="From goal to growth in eight simple moves"
            sub="A complete draft growth plan in under ten minutes — then you approve and launch." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
            {STEPS.map((s, i) => (
              <div key={s} className="srx-ccard" data-testid={`step-${i + 1}`}
                style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <span className="srx-stepnum">{i + 1}</span>
                <strong style={{ color: "#fff" }}>{s}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI AT THE CORE (from classic) */}
      <section className="srx-sec light" data-testid="ai-core">
        <div className="srx-wrap">
          <SecHead eyebrow="AI at the core" title="A team of AI agents working on your growth"
            sub="SmartReachAI doesn't just place ads — a coordinated set of AI agents plans, writes, buys, measures and follows up. Every action is explainable, and you stay in control of what runs." />
          <div className="srx-cgrid">
            {AGENTS.map((a) => (
              <div key={a.t} className="srx-ccard" data-testid={`ai-agent-${a.t.split(" ")[0].toLowerCase()}`}
                style={a.pop ? { borderColor: "var(--x-amber)" } : {}}>
                <span className={`srx-ico ${a.pop ? "amber" : "ink"}`}><a.icon size={20} /></span>
                <h3>{a.t}{a.pop && <span className="srx-badge-pop">POPULAR</span>}</h3>
                <p>{a.d}</p>
              </div>
            ))}
          </div>
          <div className="srx-note">
            <Wand2 size={22} color="var(--x-amber)" style={{ flexShrink: 0 }} />
            <p>
              Every AI recommendation shows <strong style={{ color: "#fff" }}>why</strong>, its expected impact, confidence, and what it needs your approval for.
              AI never changes budget, targeting, creative or launch state without your say-so.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT MAKES IT DIFFERENT (from classic) */}
      <section className="srx-sec dark" data-testid="different">
        <div className="srx-wrap">
          <SecHead eyebrow="What makes it different" title="Three things generic ad tools can't do for a local business" />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
            {DIFF.map((c) => (
              <Link to={c.to} key={c.title} className="srx-ccard" data-testid={`diff-${c.title.split(" ")[0].toLowerCase()}`}>
                <span className="srx-ico ink"><c.icon size={20} /></span>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
                <span className="srx-learn">Learn more <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM ENVIRONMENTS (from classic) */}
      <section className="srx-sec light" data-testid="premium-environments">
        <div className="srx-wrap">
          <SecHead eyebrow="Premium environments" title="Not just more impressions — better ones"
            sub="Brand-safe, premium inventory across every screen a customer touches." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" }}>
            {ENVIRONMENTS.map((env) => (
              <div key={env.title} className="srx-ccard srx-envcard" data-testid={`env-card-${env.title.split(" ")[0].toLowerCase()}`}>
                <h3>{env.title}</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {env.items.map((it) => <Brand key={it.name} item={it} />)}
                </div>
                <div className="srx-envnote">{env.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RADIOLIFE LOOP (from classic) */}
      <section className="srx-sec dark" data-testid="radiolife">
        <div className="srx-wrap">
          <SecHead eyebrow="RadioLife.app" title="An engagement & loyalty loop that brings customers back"
            sub="Earn → Activate → Redeem → Repeat. RadioLife.app is the consumer radio & engagement app that keeps customers coming back." />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
            {LOOP.map((s) => (
              <div key={s.t} className="srx-ccard" data-testid={`loop-${s.t.toLowerCase()}`}>
                <span className="srx-ico amber"><s.icon size={20} /></span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE MODES (from classic) */}
      <section className="srx-sec light" data-testid="service-modes">
        <div className="srx-wrap">
          <SecHead eyebrow="Your call" title="Self-Service, AI-Assisted, or a Professional team" />
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {MODES.map((m) => (
              <div key={m.t} className="srx-ccard" data-testid={`mode-${m.t.split("-")[0].toLowerCase().trim()}`}
                style={m.pop ? { borderColor: "var(--x-amber)" } : {}}>
                {m.pop && <span className="srx-badge-pop" style={{ marginLeft: 0, marginBottom: 10 }}>MOST POPULAR</span>}
                <h3>{m.t}</h3>
                <p>{m.d}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 16, fontSize: 14, color: "var(--x-muted-light)" }}>
            Switch modes anytime — your campaigns, customers, and results move with you. No migration, no duplication.
          </p>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="srx-sec dark" id="industries" data-testid="industries">
        <div className="srx-wrap">
          <div className="srx-head">
            <span className="srx-eyebrow">Built for foot traffic</span>
            <h2>Industries that live and die by who walks in</h2>
          </div>
          <div className="srx-industries">
            {INDUSTRIES.map(([label, slug]) => (
              <Link key={label} to={`/solutions/${slug}`} className="srx-chip" data-testid={`industry-${slug}`}>{label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST & METHODOLOGY (from classic) */}
      <section className="srx-sec light" data-testid="trust">
        <div className="srx-wrap">
          <SecHead eyebrow="Trust & methodology" title="Honest data. Privacy by design." />
          <div className="srx-ccard" style={{ display: "flex", gap: 16, alignItems: "flex-start", maxWidth: 820 }}>
            <ShieldCheck size={28} color="#067647" style={{ flexShrink: 0 }} />
            <p style={{ color: "var(--x-muted-light)", fontSize: 15 }}>
              We label every number as <strong>Projected</strong>, <strong>Observed</strong>, <strong>Modeled</strong>, or <strong>Verified</strong> —
              with attribution window, match rate, confidence, and limitations. Aggregate reporting only. Sensitive
              categories are blocked. We never guarantee visits or sales, and we never present a projection as a result.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="srx-footer-cta" data-testid="footer-cta">
        <div className="srx-wrap">
          <h2>Stop paying for clicks. Start paying for customers who walk in.</h2>
          <div className="srx-cta-row">
            <Link to="/signup" className="srx-btn-primary" data-testid="footer-cta-primary">Request a demo</Link>
            <Link to="/signup" className="srx-btn-secondary" data-testid="footer-cta-secondary">Get your free trade-area report</Link>
          </div>
        </div>
      </section>
    </MarketingPage>
  );
}
